/**
 * ============================================================================
 * DASHBOARD ACCESS DENIED — in-page state for a restricted dashboard.
 * ============================================================================
 *
 * Shown in the content area when the URL names a dashboard the caller cannot
 * access (spec multi-dashboard R5). We do NOT silently redirect: the whole
 * point of the URL-in-path design is that a shared "/kfe-wbr" link should tell
 * the truth about why it's not visible, and the header switcher stays mounted
 * so the user can jump to a dashboard they can see.
 *
 * Access is Bindle-lock governed, so the primary action is a Bindle
 * self-service request (routes to the resource owner for approval) rather than
 * a hand-filed ticket. The lock id comes from GetPermissions
 * (`requiredBindleLockId`); when it's the NONE sentinel or missing we omit the
 * button (nothing to request against).
 */

import React from 'react';
import Box from '@amzn/awsui-components-core/box';
import Button from '@amzn/awsui-components-core/button';
import Icon from '@amzn/awsui-components-core/icon';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import { useNavigate } from 'react-router-dom';
import { NO_LOCK_SENTINEL } from 'src/types/permissionsApi.types';
import {
  DEFAULT_DASHBOARD_SLUG,
  dashboardStructure,
} from 'src/config/dashboards';
import { WBR_DASHBOARD_ID, WBR_REVIEW_TYPE } from 'src/constants';

/** Escape-action label: the default dashboard's fallback name ("KFE - WBR"). */
const DEFAULT_DASHBOARD_LABEL = `${dashboardStructure(WBR_DASHBOARD_ID).displayName} - ${WBR_REVIEW_TYPE}`;

/** Bindle resource page for a lock id — the self-service request-access flow. */
function bindleRequestUrl(lockId: string): string {
  return `https://bindles.amazon.com/resource/${encodeURIComponent(lockId)}`;
}

interface Props {
  /** Display label of the restricted dashboard, e.g. "KFE - WBR". */
  label: string;
  /** Bindle lock id from GetPermissions; NONE/empty → no request button. */
  requiredBindleLockId?: string;
}

export const DashboardAccessDenied: React.FC<Props> = ({
  label,
  requiredBindleLockId,
}) => {
  const navigate = useNavigate();
  const hasLock =
    !!requiredBindleLockId && requiredBindleLockId !== NO_LOCK_SENTINEL;

  return (
    <div className="dashboard-access-denied" role="status">
      <SpaceBetween direction="vertical" size="m">
        <Icon name="lock-private" size="large" variant="subtle" />
        <Box variant="h2">You don't have access to {label}</Box>
        <Box color="text-body-secondary">
          This dashboard is restricted. Request access, then reload the page.
        </Box>
        <SpaceBetween direction="horizontal" size="xs">
          {hasLock && (
            <Button
              variant="primary"
              iconName="external"
              iconAlign="right"
              href={bindleRequestUrl(requiredBindleLockId!)}
              target="_blank"
            >
              Request access
            </Button>
          )}
          <Button
            variant="normal"
            onClick={() => navigate(`/${DEFAULT_DASHBOARD_SLUG}`)}
          >
            Go to {DEFAULT_DASHBOARD_LABEL}
          </Button>
        </SpaceBetween>
      </SpaceBetween>
    </div>
  );
};
